import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { HotelDescription } from 'common/dataService/types/hotelDescription';
import { TranslatedTextResponse } from 'common/chatgptFunction/types/TranslatedTextResponse';
import FetchingStatus from 'constants/statuses/FetchingStatus';

export type TranslationState = {
  selectedLanguages: string[];
  translations: TranslatedTextResponse[];
  isTranslating: boolean;
  generatedDescription: string;
};

export type HotelDescriptionsState = {
  fetching: FetchingStatus | null;
  value: HotelDescription[] | null;
  isGenerating: boolean;
  generatedDescription: string;
  translation: TranslationState;
};

const initialState: HotelDescriptionsState = {
  fetching: null,
  value: null,
  isGenerating: false,
  generatedDescription: '',
  translation: {
    selectedLanguages: [],
    translations: [],
    isTranslating: false,
    generatedDescription: '',
  },
};

function setFetchingFunction(state: HotelDescriptionsState, action: PayloadAction<FetchingStatus>) {
  return { ...state, fetching: action.payload };
}

function setDescriptionsFunction(state: HotelDescriptionsState, action: PayloadAction<HotelDescription[]>) {
  return { ...state, value: action.payload };
}

function clearDescriptionsFunction(state: HotelDescriptionsState) {
  return { ...state, value: null };
}

function setSelectedLanguagesFunction(state: HotelDescriptionsState, action: PayloadAction<string[]>) {
  return {
    ...state,
    translation: { ...state.translation, selectedLanguages: action.payload },
  };
}

function setTranslationsFunction(state: HotelDescriptionsState, action: PayloadAction<TranslatedTextResponse[]>) {
  return {
    ...state,
    translation: { ...state.translation, translations: action.payload },
  };
}

function upsertTranslationsFunction(state: HotelDescriptionsState, action: PayloadAction<TranslatedTextResponse[]>) {
  const byCode = new Map(state.translation.translations.map((t) => [t.languageCode, t]));
  action.payload.forEach((t) => byCode.set(t.languageCode, t));
  return {
    ...state,
    translation: { ...state.translation, translations: Array.from(byCode.values()) },
  };
}

function clearTranslationsFunction(state: HotelDescriptionsState) {
  return {
    ...state,
    translation: { ...state.translation, translations: [] },
  };
}

function setIsTranslatingFunction(state: HotelDescriptionsState, action: PayloadAction<boolean>) {
  return {
    ...state,
    translation: { ...state.translation, isTranslating: action.payload },
  };
}

function setIsGeneratingFunction(state: HotelDescriptionsState, action: PayloadAction<boolean>) {
  return {
    ...state,
    isGenerating: action.payload,
  };
}

function setGeneratedDescriptionFunction(state: HotelDescriptionsState, action: PayloadAction<string>) {
  return {
    ...state,
    generatedDescription: action.payload,
  };
}

function setUpdateFetchingFunction(state: HotelDescriptionsState, action: PayloadAction<FetchingStatus>) {
  return {
    ...state,
    fetching: action.payload,
  };
}

const slice = createSlice({
  name: 'hotelManager.hotelDescriptions',
  initialState,
  reducers: {
    setFetching: setFetchingFunction,
    setDescriptions: setDescriptionsFunction,
    clearDescriptions: clearDescriptionsFunction,
    setSelectedLanguages: setSelectedLanguagesFunction,
    setTranslations: setTranslationsFunction,
    upsertTranslations: upsertTranslationsFunction,
    clearTranslations: clearTranslationsFunction,
    setIsTranslating: setIsTranslatingFunction,
    setIsGenerating: setIsGeneratingFunction,
    setGeneratedDescription: setGeneratedDescriptionFunction,
    setUpdateFetching: setUpdateFetchingFunction,
  },
});

export const {
  setFetching,
  setDescriptions,
  clearDescriptions,
  setSelectedLanguages,
  setTranslations,
  upsertTranslations,
  clearTranslations,
  setIsTranslating,
  setIsGenerating,
  setGeneratedDescription,
  setUpdateFetching,
} = slice.actions;

export default slice.reducer;
